// File: src/hooks/useFirstTimeUser.js
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'baobrain_welcome_seen';

/**
 * useFirstTimeUser
 *
 * Tells you whether the user appears to be new (no tracker data yet) AND
 * whether they've already seen the Welcome Hub. The Hub should render only
 * when both:
 *   - hasNoData is true (heuristic — see below)
 *   - hasSeenWelcome is false (localStorage)
 *
 * Detection heuristic (frontend-only — no backend changes needed):
 *   We accept a `signals` object describing key data points fetched
 *   elsewhere on the page. If every signal is "empty" (0, null, [], or
 *   undefined), we treat the user as having no data yet.
 *
 *   Example call:
 *     const { hasNoData, hasSeenWelcome, dismiss } = useFirstTimeUser({
 *       totalSessions: kpis?.sessions,
 *       totalEvents: kpis?.events,
 *       prescriptions: prescriptions?.length,
 *     });
 *
 * Tradeoff: while data is loading, signals are undefined so hasNoData
 * defaults to FALSE (we wait for actual zero/empty before declaring
 * "no data" — avoids flicker of the welcome state on every page load).
 *
 * Once dismissed, we never show the Welcome Hub again for this user
 * (per-browser, since we use localStorage). Use `reset()` for testing.
 */
export function useFirstTimeUser(signals = {}, { siteId = null } = {}) {
  // Per-site dismissal: key includes site id when available so a user with
  // multiple stores doesn't permanently dismiss based on one store's flag.
  const storageKey = siteId ? `${STORAGE_KEY}_${siteId}` : STORAGE_KEY;

  const [hasSeenWelcome, setHasSeenWelcome] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false; // localStorage may be blocked (private mode, etc)
    }
  });

  // Re-read storage when siteId changes (multi-store users)
  useEffect(() => {
    try {
      setHasSeenWelcome(localStorage.getItem(storageKey) === 'true');
    } catch {
      setHasSeenWelcome(false);
    }
  }, [storageKey]);

  // Detection heuristic: data is loaded AND every signal is empty.
  // We require at least one signal to be a defined value (not undefined)
  // to avoid the flicker problem — undefined means "still loading."
  const signalValues = Object.values(signals);
  const anySignalDefined = signalValues.some((v) => v !== undefined);
  const allSignalsEmpty = signalValues.every((v) => isEmpty(v));
  const hasNoData = anySignalDefined && allSignalsEmpty;

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      /* ignore */
    }
    setHasSeenWelcome(true);
  }, [storageKey]);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
    setHasSeenWelcome(false);
  }, [storageKey]);

  return {
    hasNoData,
    hasSeenWelcome,
    shouldShowWelcome: hasNoData && !hasSeenWelcome,
    dismiss,
    reset,
  };
}

function isEmpty(v) {
  if (v === undefined) return true;
  if (v === null) return true;
  if (v === 0) return true;
  if (v === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  if (typeof v === 'object' && Object.keys(v).length === 0) return true;
  return false;
}